import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import type { RootState } from "@/core/store/store";
import {
  exchangeSsoToken,
  logout,
  redirectToSslId,
  setToken,
  ssoLogout,
} from "@/entities/session";
import { baseApi } from "@/shared/api";
import { Button, PageSpinner } from "@/shared/ui";

/** Последний полученный обменом токен: защита от цикла «бэкенд отверг токен → обмен → тот же токен». */
const LAST_EXCHANGE_KEY = "ssoLastExchange";
const LOOP_WINDOW_MS = 60_000;

const readLastExchange = (): { token?: string; at?: number } => {
  try {
    return JSON.parse(sessionStorage.getItem(LAST_EXCHANGE_KEY) || "{}");
  } catch {
    return {};
  }
};

const saveLastExchange = (token: string) => {
  try {
    sessionStorage.setItem(
      LAST_EXCHANGE_KEY,
      JSON.stringify({ token, at: Date.now() })
    );
  } catch {
    // sessionStorage недоступен — живём без защиты от цикла
  }
};

/**
 * Страницы входа на SSO-курсе: своих форм нет. Обмениваем cookie-сессию SSL ID на токен,
 * иначе уводим на id.skillslab.center (оттуда вернут на страницу, куда шёл пользователь).
 */
export const SsoGate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector(
    (state: RootState) => state.auth.isAuthenticated
  );
  const [isLoop, setIsLoop] = useState(false);
  const from = (location.state as { from?: string } | null)?.from || "/";

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
      return;
    }

    let cancelled = false;
    exchangeSsoToken().then((token) => {
      if (cancelled) return;
      if (!token) {
        redirectToSslId(from);
        return;
      }
      const last = readLastExchange();
      if (last.token === token && Date.now() - (last.at ?? 0) < LOOP_WINDOW_MS) {
        // Этот же токен курс только что отверг — повторный вход зациклится.
        setIsLoop(true);
        return;
      }
      saveLastExchange(token);
      dispatch(setToken(token));
      dispatch(baseApi.util.invalidateTags(["User"]));
      navigate(from, { replace: true });
    });
    return () => {
      cancelled = true;
    };
    // Гейт монтируется заново при каждом показе, повторный обмен не нужен
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRelogin = async () => {
    await ssoLogout();
    dispatch(logout());
    redirectToSslId(from);
  };

  if (!isLoop) return <PageSpinner text="Входим..." />;

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-lg">
        <h1 className="text-h4 mb-2 text-gray-900">Не удалось войти в курс</h1>
        <p className="text-body-sm mb-6 text-gray-600">
          Ваша сессия SSL не подходит для этого курса. Войдите заново.
        </p>
        <Button variant="primary" fullWidth onClick={handleRelogin}>
          Войти заново
        </Button>
      </div>
    </div>
  );
};
